// Field — a caption and the control it names, associated.
//
// WHY THIS EXISTS
//
// `Field` was defined 23 times across this repo, under two unrelated meanings:
// an input wrapper, and a read-only label/value display pair. That collision is
// not cosmetic — `scripts/lib/formLabels.mjs` trusted the NAME, so a control
// inside a display-only `Field` was reported as labelled when it wasn't (see
// PR #1765, which made that trust per-file).
//
// It also hid a real bug. Several of the "wrapper" copies did not wrap: they
// rendered `<label>caption</label>` and then `<input>` as a SIBLING, which
// associates nothing. Tapping the caption did not focus the field, and a screen
// reader announced the control with no name at all.
//
// So: one component, one meaning. `Field` wraps. A read-only pair is
// `<DetailRow/>`, which is a different thing with a different name.
//
// THE ASSOCIATION
//
// Implicit — the control is a DESCENDANT of the <label>. No id, no htmlFor, so
// nothing to keep unique across a list, and it survives a component being
// rendered twice on one screen. `.label` is `block …`, so the caption sits
// above its control exactly as the hand-rolled version did.
//
// "REQUIRED" AT SAVE (jump-to-required, 5 Oct)
//
// Inside a submitting <Modal>, a Save tap with a required control left empty
// does not submit: the Modal jumps to the first one, rings it red, and every
// Field holding a flagged control prints a red "Required" under it until it
// gets a value. So mark the CONTROL `required` (this component cannot reach
// into its children to do it) and give the Field `required` for the asterisk —
// never a `submitDisabled` on the Modal that greys Save out and says nothing.
//
// The line is aria-hidden on purpose. The association is implicit, so every
// word inside this <label> is part of the control's accessible NAME — an
// un-hidden "Required" would rename "Product" to "Product Required". The
// screen-reader half is the Modal's: aria-invalid on the control, plus one
// polite live region that says "<caption> is required." (see Modal.tsx). The
// caption is published as `data-field-caption` for exactly that sentence.
import { useContext, useRef, type ReactNode } from 'react'
import { RequiredFieldsContext } from '@/lib/requiredFields'

export function Field({ label, hint, required, className, captionClassName, children }: {
  /** What the control IS — "Packed on", "Grower name". Shown as the caption. */
  label:     string
  /** Optional qualifier, rendered quieter beside the caption — "(optional)". */
  hint?:     string
  /** Marks the caption with the house asterisk. Purely visual — put
   *  `required` on the control itself too: that is what a screen reader reads
   *  AND what makes a submitting <Modal> refuse Save and jump here while it is
   *  empty (see the header). */
  required?: boolean
  className?: string
  /** Caption styling, for a surface with a deliberate reason to differ — a
   *  print report's 11px caption, a dense config panel's uppercase micro-label.
   *  Defaults to the house `.label`. The ASSOCIATION is what this component
   *  centralises; the type ramp is allowed to vary where a screen means it. */
  captionClassName?: string
  /** The control. One per Field — a caption that names three inputs names
   *  none of them, so give each its own Field or its own aria-label. */
  children:  ReactNode
}) {
  const flagged = useContext(RequiredFieldsContext)
  const ref = useRef<HTMLLabelElement>(null)
  // Read on the render the Modal's context change causes — by then the label
  // has long been mounted. Nothing is flagged at mount, so the first render's
  // null ref answers correctly too.
  const missing = !!flagged && flagged.missing.some(el => ref.current?.contains(el))
  return (
    <label ref={ref} data-field-caption={label} className={className ? `block ${className}` : 'block'}>
      <span className={captionClassName ?? 'label'}>
        {label}
        {required && <span className="text-red-400"> *</span>}
        {/* Weight, not colour. This component renders on the dark app shell
            AND on light print surfaces, and no single grey is AA on both —
            text-gray-500 is 4.83:1 on white but sub-AA on gray-900, while
            .text-muted is the reverse. Inheriting the caption's own colour and
            differing by weight is correct on either ground. */}
        {hint && <span className="font-normal"> {hint}</span>}
      </span>
      {children}
      {/* Red-400 is AA on the dark shell and fails on white; red-700 is the
          reverse — so the Modal's surface picks, the same split as its own. */}
      {missing && (
        <span aria-hidden="true"
          className={`block mt-1 text-xs font-semibold ${flagged?.surface === 'light' ? 'text-red-700' : 'text-red-400'}`}>
          Required
        </span>
      )}
    </label>
  )
}
